import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import NontAuthorized401 from "NontAuthorized401";
import { getNotificationHistoryById } from "./utils/historyStorage";

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"];

const getAttachmentExtension = (attachment) => {
  const fileName = String(attachment?.name || "");
  const extension = fileName.includes(".") ? fileName.split(".").pop() : "";
  return String(extension || "").toLowerCase();
};

const isImageAttachment = (attachment) => {
  const type = String(attachment?.type || "").toLowerCase();
  return type.startsWith("image/") || IMAGE_EXTENSIONS.includes(getAttachmentExtension(attachment));
};

const getAttachmentPreview = (attachment) =>
  attachment?.previewDataUrl || attachment?.url || attachment?.previewUrl || attachment?.image || "";

const NotificationDetail = () => {
  const hasToken = Boolean(localStorage.getItem("Token"));
  const navigate = useNavigate();
  const { id } = useParams();

  const record = useMemo(() => getNotificationHistoryById(id), [id]);

  if (!hasToken) {
    return <NontAuthorized401 />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-2">
          <h3 className="text-lg font-semibold text-slate-900">Notification Campaign Detail</h3>
          <button
            type="button"
            onClick={() => navigate("/notification-list")}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
          >
            Back To List
          </button>
        </div>

        {!record ? (
          <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">
            Campaign record not found.
          </p>
        ) : (
          <div className="space-y-4">
            {record?.isDemo ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                This is a demo notification record stored locally for UI testing.
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-md border border-slate-200 p-3">
                <p className="text-xs font-semibold uppercase text-slate-500">Sent At</p>
                <p className="mt-1 text-sm text-slate-800">{formatDateTime(record?.sentAt)}</p>
              </div>
              <div className="rounded-md border border-slate-200 p-3">
                <p className="text-xs font-semibold uppercase text-slate-500">Channel</p>
                <p className="mt-1 text-sm capitalize text-slate-800">{record?.channel || "-"}</p>
              </div>
              <div className="rounded-md border border-slate-200 p-3">
                <p className="text-xs font-semibold uppercase text-slate-500">Module</p>
                <p className="mt-1 text-sm capitalize text-slate-800">{record?.module || "-"}</p>
              </div>
              <div className="rounded-md border border-slate-200 p-3">
                <p className="text-xs font-semibold uppercase text-slate-500">Recipients</p>
                <p className="mt-1 text-sm text-slate-800">{record?.totalRecipients || 0}</p>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase text-slate-500">Target</p>
              <p className="mt-1 text-sm text-slate-800">{record?.target || "-"}</p>
            </div>

            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase text-slate-500">Subject</p>
              <p className="mt-1 text-sm text-slate-800">{record?.subject || "-"}</p>
            </div>

            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase text-slate-500">Message</p>
              <pre className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800">{record?.message || "-"}</pre>
            </div>

            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase text-slate-500">Attachments</p>
              {Array.isArray(record?.attachments) && record.attachments.length ? (
                <div className="mt-3 flex flex-wrap gap-3">
                  {record.attachments.map((attachment, index) => {
                    const preview = getAttachmentPreview(attachment);
                    const isImage = isImageAttachment(attachment);
                    const extension = getAttachmentExtension(attachment).toUpperCase() || "FILE";

                    return (
                      <div
                        key={`${attachment?.name || "file"}-${index}`}
                        className="w-28 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 shadow-sm"
                      >
                        {isImage && preview ? (
                          <a href={preview} target="_blank" rel="noreferrer" className="block h-20 w-full bg-white">
                            <img
                              src={preview}
                              alt={attachment?.name || "Attachment"}
                              className="h-full w-full object-cover"
                            />
                          </a>
                        ) : (
                          <div className="flex h-20 w-full items-center justify-center bg-white px-2 text-center text-xs font-semibold text-slate-500">
                            {extension}
                          </div>
                        )}

                        <div className="p-2">
                          <p className="truncate text-xs font-semibold text-slate-800">
                            {attachment?.name || "Attachment"}
                          </p>
                          <p className="mt-1 text-[11px] text-slate-500">
                            {Math.max(1, Math.round((attachment?.size || 0) / 1024))} KB
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="mt-1 text-sm text-slate-500">No attachments</p>
              )}
            </div>

            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase text-slate-500">Selected Recipients</p>
              {Array.isArray(record?.recipients) && record.recipients.length ? (
                <div className="mt-2 max-h-60 overflow-auto rounded-md border border-slate-200">
                  <ul className="divide-y divide-slate-100">
                    {record.recipients.map((recipient, index) => (
                      <li key={`${recipient?.id || "recipient"}-${index}`} className="px-3 py-2">
                        <p className="text-sm font-medium text-slate-800">{recipient?.name || "Unknown"}</p>
                        <p className="text-xs text-slate-500">{recipient?.meta || "-"}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="mt-1 text-sm text-slate-500">No recipient details available</p>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NotificationDetail;
